import { useState } from "react";
import { useSearchProductsKomoran } from "@/hooks/use-orders";
import { useActiveStore } from "@/hooks/use-active-store";
import { useDebounce } from "@/hooks/use-debounce";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Package } from "lucide-react";

interface AddItemDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (productId: number, variationId: number | undefined, quantity: number) => Promise<void>;
}

export function AddItemDialog({ open, onClose, onAdd }: AddItemDialogProps) {
  const { storeId } = useActiveStore();
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [selectedVariation, setSelectedVariation] = useState<any | null>(null);
  const [expandedProductId, setExpandedProductId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  const debouncedSearch = useDebounce(search);
  const { data: products, isLoading } = useSearchProductsKomoran(storeId, debouncedSearch);

  function formatAttributes(attributes?: { option?: string }[]) {
    return attributes?.map((attr) => attr.option).filter(Boolean).join(" / ") || "";
  }

  function selectProduct(product: any, variation?: any) {
    setSelectedProduct(product);
    setSelectedVariation(variation ?? null);
  }

  function handleProductClick(product: any) {
    if (product.type === "variable" && product.variations?.length > 0) {
      setSelectedProduct(null);
      setSelectedVariation(null);
      setExpandedProductId((cur) => (cur === product.id ? null : product.id));
    } else {
      selectProduct(product);
    }
  }

  async function handleAdd() {
    if (!selectedProduct) return;
    setAdding(true);
    try {
      await onAdd(selectedProduct.id, selectedVariation?.id, quantity);
      handleClose();
    } finally {
      setAdding(false);
    }
  }

  function handleClose() {
    setSearch("");
    setSelectedProduct(null);
    setSelectedVariation(null);
    setExpandedProductId(null);
    setQuantity(1);
    onClose();
  }

  const selectedName =
    selectedProduct &&
    [selectedProduct.name, selectedVariation ? formatAttributes(selectedVariation.attributes) : ""]
      .filter(Boolean)
      .join(" — ");
  const selectedSku = selectedVariation?.sku || selectedProduct?.sku || "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dodaj stavku</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Pretraži proizvode</Label>
            <Input
              placeholder="Pretraži proizvode po nazivu ili SKU..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedProduct(null);
                setSelectedVariation(null);
                setExpandedProductId(null);
              }}
            />
          </div>

          {search.length >= 2 && (
            <div className="ring-1 ring-foreground/10 max-h-64 overflow-y-auto">
              {isLoading && (
                <p className="p-3 text-xs text-muted-foreground">Učitavanje...</p>
              )}
              {!isLoading && (!products || products.length === 0) && (
                <p className="p-3 text-xs text-muted-foreground">Nema rezultata</p>
              )}
              {!isLoading &&
                products?.map((product: any) => (
                  <div key={product.id}>
                    <button
                      type="button"
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 border-b last:border-0 transition-colors flex items-center gap-2 ${
                        selectedProduct?.id === product.id && !selectedVariation ? "bg-muted" : ""
                      }`}
                      onClick={() => handleProductClick(product)}
                    >
                      {product.image ? (
                        <img src={product.image} alt="" className="w-8 h-8 object-cover rounded shrink-0" />
                      ) : (
                        <div className="w-8 h-8 bg-muted rounded flex items-center justify-center shrink-0">
                          <Package className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{product.name}</p>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          {product.sku && <span>SKU: {product.sku}</span>}
                          {product.type === "variable" && product.variations?.length > 0 ? (
                            <span className="text-primary">{product.variations.length} varijanti ›</span>
                          ) : product.stock_status === "outofstock" ? (
                            <span className="text-destructive">Nema na stanju</span>
                          ) : product.manage_stock && product.stock_quantity != null ? (
                            <span>Stanje: {product.stock_quantity}</span>
                          ) : (
                            <span className="text-green-600">Na stanju</span>
                          )}
                        </div>
                      </div>
                      <span className="font-medium shrink-0">
                        {product.price ? `${parseFloat(product.price).toFixed(2)} KM` : "—"}
                      </span>
                    </button>
                    {expandedProductId === product.id &&
                      product.variations?.map((variation: any) => (
                        <button
                          key={variation.id}
                          type="button"
                          className={`w-full text-left pl-12 pr-3 py-2 text-xs hover:bg-muted/50 border-b last:border-0 transition-colors flex items-center gap-2 ${
                            selectedVariation?.id === variation.id ? "bg-muted" : "bg-muted/20"
                          }`}
                          onClick={() => selectProduct(product, variation)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">
                              {formatAttributes(variation.attributes) || "Varijanta"}
                            </p>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              {variation.sku && <span>SKU: {variation.sku}</span>}
                              {variation.stock_status === "outofstock" ? (
                                <span className="text-destructive">Nema na stanju</span>
                              ) : variation.stock_quantity != null ? (
                                <span>Stanje: {variation.stock_quantity}</span>
                              ) : (
                                <span className="text-green-600">Na stanju</span>
                              )}
                            </div>
                          </div>
                          <span className="font-medium shrink-0">
                            {variation.price ? `${parseFloat(variation.price).toFixed(2)} KM` : "—"}
                          </span>
                        </button>
                      ))}
                  </div>
                ))}
            </div>
          )}

          {selectedProduct && (
            <div className="flex items-center gap-3 bg-muted/30 px-3 py-2">
              <div className="flex-1 text-xs min-w-0">
                <p className="font-medium truncate">{selectedName}</p>
                {selectedSku && <p className="text-muted-foreground">SKU: {selectedSku}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Količina</Label>
                <Input
                  type="number"
                  min={1}
                  className="h-7 w-16 text-xs text-center p-1"
                  value={quantity}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setQuantity(Number.isFinite(next) ? Math.max(1, next) : 1);
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleClose}>
              Odustani
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={!selectedProduct || adding}>
              {adding ? "Dodavanje..." : "Dodaj"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
